import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError, companies, sandbox } from "../api/client";
import type { Company, KybCase } from "../api/types";
import { companyCanWrite, sandboxEnabled, useMe, useSession } from "../auth/session";
import { BarePage } from "../components/Shell";
import { ErrorBox, Field, Icon, Pill, Spinner, errorMessage, useToast } from "../components/ui";
import { usePolling } from "../hooks/useAsync";
import { fmt, t } from "../i18n";
import { formatCNPJ } from "../lib/format";
import { formatCents } from "../lib/money";
import { isValidCNPJ } from "../lib/validators";

// Empresa e termos; sócios, documentos e atividade são coletados pela Lumx no fluxo de verificação dela
const TOTAL_STEPS = 2;
const STORAGE_KEY = "fxp.onboarding";

interface Draft {
  step: number;
  tradeName: string;
  incorporationDate: string;
}

function loadDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const d = JSON.parse(raw) as Partial<Draft>;
      return { step: d.step ?? 1, tradeName: d.tradeName ?? "", incorporationDate: d.incorporationDate ?? "" };
    }
  } catch {
    /* ignore */
  }
  return emptyDraft();
}
function emptyDraft(): Draft {
  return { step: 1, tradeName: "", incorporationDate: "" };
}

export function Cadastro() {
  const me = useMe();
  const { refresh } = useSession();
  const company = me.company ?? null;
  const [draft, setDraft] = useState<Draft>(loadDraft);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
      /* ignore */
    }
  }, [draft]);

  const patch = useCallback((p: Partial<Draft> | ((d: Draft) => Partial<Draft>)) => setDraft((d) => ({ ...d, ...(typeof p === "function" ? p(d) : p) })), []);
  const goto = useCallback((step: number) => setDraft((d) => ({ ...d, step })), []);

  const inWizard = !company || company.status === "draft" || editing;
  const step = !company ? 1 : Math.min(Math.max(draft.step, 1), TOTAL_STEPS);
  const canWrite = !company || companyCanWrite(me);

  if (!inWizard) {
    return (
      <StatusScreen
        company={company}
        onEdit={() => {
          setEditing(true);
          goto(2);
        }}
        onRefresh={refresh}
      />
    );
  }

  return (
    <BarePage title={t.onboarding.title} right={<span className="muted small">{fmt(t.onboarding.stepOf, { n: step, total: TOTAL_STEPS })}</span>}>
      <div className="two n360">
        <div className="card" style={{ padding: "26px 28px", display: "flex", flexDirection: "column", gap: 22 }}>
          <Wizard step={step} onGoto={(s) => company && s <= step && goto(s)} />
          {!canWrite && <div className="form-error">{t.onboarding.viewerCannot}</div>}
          {step === 1 && <StepEmpresa company={company} draft={draft} patch={patch} onNext={() => goto(2)} canWrite={canWrite} />}
          {step === 2 && (
            <StepTermos
              onBack={() => goto(1)}
              canWrite={canWrite}
              onDone={async () => {
                setEditing(false);
                try {
                  sessionStorage.removeItem(STORAGE_KEY);
                } catch {
                  /* ignore */
                }
                await refresh();
              }}
            />
          )}
        </div>
        <div className="stack">
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10, fontSize: 13.5, lineHeight: 1.5 }}>
            <h2 style={{ fontSize: 15 }}>{t.onboarding.whatNext}</h2>
            <span className="ink2">{t.onboarding.whatNext1}</span>
            <span className="ink2">{t.onboarding.whatNext2}</span>
            <span className="ink2">{t.onboarding.whatNext3}</span>
          </div>
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8, fontSize: 13, lineHeight: 1.5 }}>
            <span className="lbl">{t.onboarding.privacy}</span>
            <span className="ink2">{t.onboarding.privacyText}</span>
          </div>
        </div>
      </div>
    </BarePage>
  );
}

function Wizard({ step, onGoto }: { step: number; onGoto: (s: number) => void }) {
  return (
    <div className="wizard" role="list">
      {t.onboarding.steps.map((label, i) => {
        const n = i + 1;
        const cls = n < step ? "done" : n === step ? "now" : "";
        return (
          <button type="button" key={n} className={`wstep ${cls}`} onClick={() => onGoto(n)} disabled={n >= step} aria-current={n === step ? "step" : undefined} role="listitem">
            <span className="n">{n < step ? Icon.check() : n}</span>
            <span className="txt">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function StepNav({ onBack, next, pending, disabled }: { onBack?: () => void; next: string; pending?: boolean; disabled?: boolean }) {
  return (
    <div className="row between" style={{ paddingTop: 6 }}>
      {onBack ? (
        <button type="button" className="btn ghost" onClick={onBack}>
          {t.common.back}
        </button>
      ) : (
        <span />
      )}
      <div className="row">
        <span className="muted small">{t.onboarding.saveLater}</span>
        <button type="submit" className="btn" disabled={pending || disabled}>
          {Icon.arrow()}
          {pending ? t.common.loading : next}
        </button>
      </div>
    </div>
  );
}

function CompanyFound({ c }: { c: Company | NonNullable<ReturnType<typeof useMe>["company"]> }) {
  const parts = [`CNPJ ${formatCNPJ(c.cnpj)}`];
  if ("cnae" in c && c.cnae) parts.push(`CNAE ${c.cnae}`);
  if ("receitaStatus" in c && c.receitaStatus) parts.push(`Situação ${c.receitaStatus}`);
  return (
    <div className="stack s8">
      <span className="okmark">
        {Icon.check()}
        {t.onboarding.companyFound}
      </span>
      <h2 style={{ fontSize: 20, fontWeight: 800 }}>{c.legalName}</h2>
      <span className="ink2" style={{ fontSize: 13.5 }}>
        {parts.join(" · ")}
      </span>
    </div>
  );
}

function StepEmpresa({ company, draft, patch, onNext, canWrite }: { company: ReturnType<typeof useMe>["company"]; draft: Draft; patch: (p: Partial<Draft>) => void; onNext: () => void; canWrite: boolean }) {
  const { refresh } = useSession();
  const [cnpj, setCnpj] = useState(company?.cnpj ? formatCNPJ(company.cnpj) : "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Company | null>(null);
  const found = company ?? created;
  // data de constituição vem da consulta à Receita quando existe; o cliente só confirma
  const incDate = draft.incorporationDate || (found && "incorporationDate" in found ? found.incorporationDate?.slice(0, 10) : "") || "";

  async function lookup(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isValidCNPJ(cnpj)) return setError(t.onboarding.cnpjInvalid);
    setPending(true);
    try {
      const r = await companies.create(cnpj.replace(/\D/g, ""));
      setCreated(r.company);
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  async function next(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (canWrite) await companies.update({
        tradeName: draft.tradeName || undefined,
        incorporationDate: incDate || undefined,
      });

      onNext();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  if (!found) {
    return (
      <form onSubmit={lookup} className="stack" noValidate>
        <h2 style={{ fontSize: 20, fontWeight: 800 }}>{t.onboarding.steps[0]}</h2>
        <Field label={t.onboarding.cnpj} error={error}>
          {(id, d) => (
            <div className={`inp ${error ? "err" : ""}`}>
              <input id={id} className="mono" inputMode="numeric" placeholder={t.onboarding.cnpjPlaceholder} value={cnpj} onChange={(e) => setCnpj(formatCNPJ(e.target.value))} aria-describedby={d} autoFocus />
            </div>
          )}
        </Field>
        <StepNav next={pending ? t.onboarding.lookingUp : t.onboarding.lookup} pending={pending} />
      </form>
    );
  }

  return (
    <form onSubmit={next} className="stack" noValidate>
      <CompanyFound c={found} />
      <div className="grid2">
        <Field label={t.onboarding.tradeName}>
          {(id) => (
            <div className="inp">
              <input id={id} value={draft.tradeName} onChange={(e) => patch({ tradeName: e.target.value })} disabled={!canWrite} />
            </div>
          )}
        </Field>
        <Field label={t.onboarding.incorporationDate} hint={t.onboarding.incorporationDateHint}>
          {(id, d) => (
            <div className="inp">
              <input id={id} type="date" value={incDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => patch({ incorporationDate: e.target.value })} aria-describedby={d} disabled={!canWrite} />
            </div>
          )}
        </Field>
      </div>
      <span className="ink2" style={{ fontSize: 13 }}>{t.onboarding.partnerCompanyFields}</span>
      {error && <ErrorBox error={new Error(error)} />}
      <StepNav next={fmt(t.onboarding.continueTo, { step: t.onboarding.steps[1].toLowerCase() })} pending={pending} />
    </form>
  );
}

function StepTermos({ onBack, onDone, canWrite }: { onBack: () => void; onDone: () => Promise<void>; canWrite: boolean }) {
  const me = useMe();
  const [accepted, setAccepted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const legal = me.legalTexts ?? {};
  const version = legal.termsVersion || t.brand.termsVersionDefault;
  const text = legal.terms || legal.partnerDisclosure || t.brand.partnerDisclosureDefault;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!accepted) return setError(t.onboarding.termsMustAccept);
    setPending(true);
    try {
      await companies.submitKyb(version);
      await onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="stack" noValidate>
      <h2 style={{ fontSize: 20, fontWeight: 800 }}>{t.onboarding.termsTitle}</h2>
      <div className="card" style={{ padding: 16, maxHeight: 280, overflow: "auto", fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-2)", whiteSpace: "pre-wrap" }}>
        {text}
        {"\n\n"}
        {t.brand.fiscal}
      </div>
      <p className="ink2" style={{ fontSize: 13 }}>
        {t.onboarding.termsReadFull} <Link to="/termos" target="_blank" rel="noopener">{t.legal.terms}</Link> · <Link to="/privacidade" target="_blank" rel="noopener">{t.legal.privacy}</Link>
      </p>
      <label className="check">
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} disabled={!canWrite} />
        <span>{fmt(t.onboarding.termsAccept, { v: version })}</span>
      </label>
      {error && <ErrorBox error={new Error(error)} />}
      <div className="row between" style={{ paddingTop: 6 }}>
        <button type="button" className="btn ghost" onClick={onBack}>
          {t.common.back}
        </button>
        <button type="submit" className="btn" disabled={pending || !canWrite}>
          {pending ? t.onboarding.submitting : t.onboarding.submit}
        </button>
      </div>
    </form>
  );
}

function StatusScreen({ company, onEdit, onRefresh }: { company: NonNullable<ReturnType<typeof useMe>["company"]>; onEdit: () => void; onRefresh: () => Promise<unknown> }) {
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const [kyb, setKyb] = useState<KybCase | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const status = company.status;
  const pendingStatus = status === "submitted" || status === "in_review";

  const load = useCallback(async () => {
    try {
      const r = await companies.kyb();
      setKyb(r.kybCase ?? null);
      setErr(null);
      const s = r.kybCase?.status;
      if (s && s !== status) await onRefresh();
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) setErr(e);
    } finally {
      setLoading(false);
    }
  }, [status, onRefresh]);

  useEffect(() => {
    void load();
  }, [load]);
  usePolling(load, 5000, pendingStatus);

  const tone = status === "approved" ? "ok" : status === "rejected" || status === "blocked" ? "crit" : "warn";
  const reasons = kyb?.reasons ?? [];
  const flags = kyb?.flags ?? [];

  async function decide(s: string) {
    if (!kyb) return;
    try {
      await sandbox.kybDecide(kyb.id, s);
      toast(t.sandbox.done);
      await load();
      await onRefresh();
    } catch (e) {
      toast(fmt(t.sandbox.failed, { msg: errorMessage(e) }));
    }
  }

  return (
    <BarePage title={t.onboarding.title}>
      <div className="two n360">
        <div className="card" style={{ padding: "26px 28px", display: "flex", flexDirection: "column", gap: 18 }}>
          <div className="row">
            <Pill tone={tone}>{t.toca.kybStatus[status] ?? status}</Pill>
            {pendingStatus && <span className="spin" aria-hidden="true" />}
          </div>
          <div className="stack s8">
            <h2 style={{ fontSize: 22, fontWeight: 800 }}>{t.onboarding.statusTitle[status]}</h2>
            <span className="ink2" style={{ fontSize: 14, lineHeight: 1.5 }}>{t.onboarding.statusBody[status]}</span>
          </div>
          <div className="ink2" style={{ fontSize: 13.5 }}>
            <b>{company.legalName}</b> · CNPJ {formatCNPJ(company.cnpj)}
          </div>
          {loading && <Spinner />}
          {err ? <ErrorBox error={err} onRetry={load} /> : null}
          {reasons.length > 0 && (
            <div className="stack s8">
              <span className="lbl">{t.onboarding.reasonsTitle}</span>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.6 }}>
                {reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {flags.length > 0 && (
            <div className="stack s8">
              <span className="lbl">{t.onboarding.flagsTitle}</span>
              <div className="row">
                {flags.map((f, i) => (
                  <Pill key={i} tone="warn">
                    {f}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {kyb?.decisionNote && <div className="form-info">{kyb.decisionNote}</div>}
          {kyb?.partnerUrls && kyb.partnerUrls.length > 0 && (pendingStatus || status === "needs_more_info") && (
            <div className="stack s8">
              <span className="lbl">{t.onboarding.partnerStepsTitle}</span>
              <div className="ink2" style={{ fontSize: 13.5 }}>{t.onboarding.partnerStepsLead}</div>
              <div className="row">
                {kyb.partnerUrls.filter((u) => /^https:\/\//i.test(u.url)).map((u) => (
                  <a key={u.url} className="btn ghost sm" href={u.url} target="_blank" rel="noopener noreferrer">{u.label}</a>
                ))}
              </div>
            </div>
          )}
          <div className="row">
            {status === "approved" && (
              <button type="button" className="btn" onClick={() => navigate("/")}>
                {t.onboarding.goPanel}
              </button>
            )}
            {status === "needs_more_info" && companyCanWrite(me) && !kyb?.partnerUrls?.length && (
              <button type="button" className="btn" onClick={onEdit}>
                {t.onboarding.resubmit}
              </button>
            )}
            {pendingStatus && (
              <button type="button" className="btn ghost" onClick={load}>
                {t.onboarding.checkStatus}
              </button>
            )}
            <button type="button" className="btn ghost" onClick={() => navigate("/configuracoes")}>
              {t.nav.configuracoes}
            </button>
          </div>
          {sandboxEnabled(me) && kyb && pendingStatus && (
            <div className="card" style={{ padding: 14, background: "var(--bg)", display: "flex", flexDirection: "column", gap: 8 }}>
              <span className="lbl" style={{ color: "var(--mel-ink)" }}>{t.onboarding.sandboxDecide}</span>
              <div className="row">
                <button type="button" className="btn ghost sm" onClick={() => decide("approved")}>{t.onboarding.approve}</button>
                <button type="button" className="btn ghost sm" onClick={() => decide("needs_more_info")}>{t.onboarding.needsInfo}</button>
                <button type="button" className="btn ghost sm" onClick={() => decide("rejected")}>{t.onboarding.reject}</button>
              </div>
            </div>
          )}
        </div>
        <div className="stack">
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10, fontSize: 13.5, lineHeight: 1.5 }}>
            <h2 style={{ fontSize: 15 }}>{t.onboarding.whatNext}</h2>
            <span className="ink2">{t.onboarding.whatNext2}</span>
            <span className="ink2">{fmt("Limites iniciais: {tx} por pagamento e {m} por mês.", { tx: formatCents(company.limits.perTxCents, { symbol: "R$", decimals: false }), m: formatCents(company.limits.monthlyCents, { symbol: "R$", decimals: false }) })}</span>
          </div>
        </div>
      </div>
    </BarePage>
  );
}
